import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useMessages, useSendMessage, useMarkAsRead } from '@/hooks/useMessages';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import type { MessageResponse, ChallengeMessageMetadata } from '@/lib/api';
import { GameIcon } from '@/components/ui/GameIcon';
import { getGameName } from '@/lib/gameConfig';

function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const HEADER_HEIGHT = 64; // 4rem

const STATUS_LABELS: Record<string, string> = {
  open: 'Waiting for opponent',
  agreed: 'Agreed — Fund now',
  funding: 'Funding in progress',
  locked: 'Locked — Ready to play',
  in_progress: 'Match in progress',
  pending_verification: 'Verifying result',
  ready_to_collect: 'Ready to collect',
  settled: 'Settled',
  cancelled: 'Cancelled',
  voided: 'Voided',
  disputed: 'Disputed',
  pending: 'Pending response',
  countered: 'Counter offer',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

function ChallengeCard({
  message,
  friendName,
}: {
  message: MessageResponse;
  isCurrentUser: boolean;
  friendName: string;
}) {
  const navigate = useNavigate();
  const meta = message.metadata as ChallengeMessageMetadata | null;

  if (!meta) return null;

  const isSender = message.senderId === useAuthStore.getState().user?.id;
  const isAccepted = meta.status === 'accepted' || message.type === 'challenge_accepted';
  const isDeclined = meta.status === 'declined' || message.type === 'challenge_declined';
  const isExpired = meta.status === 'expired' || message.type === 'challenge_expired';
  const isCountered = meta.status === 'countered' || message.type === 'challenge_countered';
  const isTerminal = isDeclined || isExpired || meta.status === 'cancelled' || meta.status === 'voided';

  // Contextual description based on who's viewing
  const gameName = getGameName(meta.gameCode);
  let description = '';
  if (message.type === 'challenge_sent') {
    description = isSender
      ? `You challenged ${friendName} to a ${gameName} match`
      : `${message.senderName} challenged you to a ${gameName} match`;
  } else if (message.type === 'challenge_accepted') {
    description = isSender
      ? `You accepted the ${gameName} challenge`
      : `${message.senderName} accepted the ${gameName} challenge`;
  } else if (message.type === 'challenge_declined') {
    description = isSender
      ? `You declined the ${gameName} challenge`
      : `${message.senderName} declined the challenge`;
  } else if (message.type === 'challenge_countered') {
    description = isSender
      ? `You countered with $${meta.amount} ${meta.currency}`
      : `${message.senderName} countered with $${meta.amount} ${meta.currency}`;
  } else if (message.type === 'challenge_expired') {
    description = 'Challenge expired';
  }

  const statusLabel = STATUS_LABELS[meta.status] || meta.status;

  return (
    <div className="mx-auto my-2 w-full max-w-[85%]">
      <div
        className={cn(
          'rounded-2xl border p-4 space-y-3',
          isAccepted && 'border-green-500/30 bg-green-500/5',
          isTerminal && 'border-muted bg-muted/30 opacity-70',
          isCountered && !isTerminal && 'border-yellow-500/30 bg-yellow-500/5',
          !isAccepted && !isTerminal && !isCountered && 'border-primary/30 bg-primary/5'
        )}
      >
        <div className="flex items-center gap-3">
          <GameIcon gameCode={meta.gameCode} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{gameName}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold">${meta.amount}</p>
            <p className="text-[10px] text-muted-foreground">{meta.currency}</p>
          </div>
        </div>

        {/* Status badge */}
        <div className="flex items-center justify-center">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              meta.status === 'accepted' || meta.status === 'settled' || meta.status === 'ready_to_collect'
                ? 'bg-green-500/10 text-green-500'
                : meta.status === 'declined' || meta.status === 'cancelled' || meta.status === 'voided' || meta.status === 'expired'
                  ? 'bg-muted text-muted-foreground'
                  : meta.status === 'countered'
                    ? 'bg-yellow-500/10 text-yellow-500'
                    : meta.status === 'in_progress' || meta.status === 'locked'
                      ? 'bg-blue-500/10 text-blue-500'
                      : 'bg-primary/10 text-primary'
            )}
          >
            {statusLabel}
          </span>
        </div>

        {/* View Wager button — shown when a wager exists */}
        {meta.wagerId && (
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={() => navigate(`/wagers/${meta.wagerId}`)}
          >
            <ExternalLink className="mr-1.5 h-4 w-4" />
            View Wager
          </Button>
        )}

        <p className="text-center text-[10px] text-muted-foreground">
          {formatMessageTime(message.createdAt)}
        </p>
      </div>
    </div>
  );
}

export function ChatPage() {
  const { id: friendId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [input, setInput] = useState('');

  const { data: messages, isLoading } = useMessages(friendId);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead(friendId);

  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMarkedRead = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only apply visualViewport logic on mobile (< 1024px)
  const isMobile = useCallback(() => window.innerWidth < 1024, []);

  const updateHeight = useCallback(() => {
    const el = containerRef.current;
    if (!el || !isMobile()) return;

    const vv = window.visualViewport;
    if (vv) {
      const availableHeight = vv.height;
      const topOffset = vv.offsetTop;
      el.style.top = `${topOffset + HEADER_HEIGHT}px`;
      el.style.height = `${Math.max(availableHeight - HEADER_HEIGHT, 200)}px`;
      el.style.bottom = 'auto';

      // Scroll to bottom after layout update
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile()) return;
    const vv = window.visualViewport;
    if (!vv) return;

    updateHeight();
    vv.addEventListener('resize', updateHeight);
    vv.addEventListener('scroll', updateHeight);
    return () => {
      vv.removeEventListener('resize', updateHeight);
      vv.removeEventListener('scroll', updateHeight);
    };
  }, [updateHeight, isMobile]);

  // Mark messages as read on mount
  useEffect(() => {
    if (friendId && !hasMarkedRead.current) {
      hasMarkedRead.current = true;
      markAsRead.mutate();
    }
  }, [friendId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    const content = input.trim();
    if (!content || !friendId) return;

    sendMessage.mutate({ friendId, content });
    setInput('');
    // Keep focus on input so keyboard stays open on mobile
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Derive friend info from first received message or fallback
  const friendName =
    messages?.find((m) => m.senderId !== user?.id)?.senderName || 'Chat';
  const friendAvatar =
    messages?.find((m) => m.senderId !== user?.id)?.senderAvatar || null;

  const isChallengeMessage = (type: string | undefined) =>
    type && type !== 'text';

  return (
    <div
      ref={containerRef}
      className="fixed inset-x-0 top-16 bottom-0 z-40 flex flex-col bg-background lg:relative lg:inset-auto lg:z-auto lg:h-[calc(100vh-7rem)]"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/messages')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Avatar className="h-8 w-8">
          <AvatarImage src={friendAvatar || undefined} alt={friendName} />
          <AvatarFallback>
            {friendName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <span className="font-semibold">{friendName}</span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3"
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No messages yet. Say hello!
          </div>
        ) : (
          messages.map((message, index) => {
            const isSent = message.senderId === user?.id;

            // Challenge messages render as centered cards
            if (isChallengeMessage(message.type)) {
              return (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.5) }}
                >
                  <ChallengeCard
                    message={message}
                    isCurrentUser={isSent}
                    friendName={friendName}
                  />
                </motion.div>
              );
            }

            // Regular text messages
            return (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.02, 0.5) }}
                className={cn('flex', isSent ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-4 py-2',
                    isSent
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.content}
                  </p>
                  <p
                    className={cn(
                      'mt-1 text-[10px]',
                      isSent
                        ? 'text-primary-foreground/70'
                        : 'text-muted-foreground'
                    )}
                  >
                    {formatMessageTime(message.createdAt)}
                  </p>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Input — pinned to bottom of flex container */}
      <div className="shrink-0 border-t bg-background px-4 py-3 safe-area-pb">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sendMessage.isPending}
            className="flex-1 text-base"
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || sendMessage.isPending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
